import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost } from '@/features/shared/utils/dataFetching'
import {
  type IdentityRequestSource,
  type RegulationCategoryStateAndReason,
} from '@/features/account/services/DataPrivacy/types'
import { useUserId } from '@/features/account/services/User/hooks'
import { restrictionsQueryOptions } from '@/features/account/services/DataPrivacy/queries'
import { isOnServer } from '@shared/constants/util'

export const useMutationSendOptOutRequest = <TRequestSource extends IdentityRequestSource>() => {
  const userId = useUserId()

  return useMutation({
    mutationFn: (
      requestParams: TRequestSource extends 'web-restriction'
        ? RegulationCategoryStateAndReason & { request_source: TRequestSource }
        : { request_source: TRequestSource },
    ) => {
      return apiPost({
        config: {
          url: 'identitywf/v1/request',
          data: {
            ...requestParams,
            user_id: String(userId),
            right_type: 'do_not_share_sell_ta',
          },
        },
        fetcherName: 'useMutationSendOptOutRequest',
      })
    },
  })
}

const handleSetWindowDoNotShareSellTa = () => {
  if (!isOnServer()) window.SHIPT_DO_NOT_SHARE_SELL_TA = true
}

export const useMutationHandleOptOutRestrictions = (onOptOutNotRequired?: () => Promise<void>) => {
  const userId = useUserId()
  const queryClient = useQueryClient()
  const { mutateAsync: sendOptOutRequest } = useMutationSendOptOutRequest()

  const handleOptOutRequests = (params: RegulationCategoryStateAndReason) => {
    handleSetWindowDoNotShareSellTa()
    if (userId) {
      sendOptOutRequest({ ...params, request_source: 'web-restriction' })
    }
    return queryClient.invalidateQueries()
  }

  return useMutation({
    mutationFn: (params: Parameters<typeof restrictionsQueryOptions>[0]) =>
      queryClient.query(restrictionsQueryOptions(params)),
    onSuccess: async (doNotShareSellTaRestriction) => {
      if (doNotShareSellTaRestriction) {
        return handleOptOutRequests({
          regulation_category: doNotShareSellTaRestriction.regulation_category,
          regulation_reason: doNotShareSellTaRestriction.regulation_reason,
          regulation_state: doNotShareSellTaRestriction.regulation_state,
        })
      } else {
        return onOptOutNotRequired?.()
      }
    },
    onError: () => {
      return handleOptOutRequests({
        regulation_state: '',
        regulation_category: '',
        regulation_reason: 'service_error' as const,
      })
    },
  })
}
